import _ from 'underscore';
import classNames from 'classnames';
import React, { useCallback, useContext, useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';

export function useConfirmDialog(defaultProps, instrumentationInfo = {}) {
  const [additionalProps, setAdditionalProps] = useState({ hidden: true });
  const {
    content,
    className,
    hideCancel = false,
    hideConfirm = false,
    onConfirm,
    onCancel: originalOnCancel,
    secondaryButtons = [],
    onDisplay,
    modalType = 'modal',
    ...passThroughProps
  } = {
    ...defaultProps,
    ...additionalProps,
  };

  const {
    activity,
    dialogName,
  } = instrumentationInfo;
  const i18n = useContext(I18nContext);

  const showDialog = useCallback((periodProps = {}) => {
    if (activity) {
      activity.trace(`Showing dialog "${dialogName}".`);
    }

    setAdditionalProps({
      ...periodProps,
      hidden: false,
    });

    if (onDisplay && _.isFunction(onDisplay)) {
      onDisplay();
    }
  }, [setAdditionalProps, activity, dialogName, onDisplay]);

  const dismissDialog = useCallback((buttonKey) => {
    if (activity) {
      activity.trace(`Dialog "${dialogName}" dismissed with button "${buttonKey}".`);
    }

    // Keep additionalProps here to avoid the dialog renders again
    //   without additionalProps before hidding.
    setAdditionalProps({
      ...additionalProps,
      hidden: true,
    });
  }, [additionalProps, setAdditionalProps, activity, dialogName]);

  const internalOnCancel = useCallback(() => {
    dismissDialog('cancel');

    if (originalOnCancel) {
      originalOnCancel();
    }
  }, [dismissDialog, originalOnCancel]);

  const onConfirmClicked = useCallback(() => {
    dismissDialog('confirm');

    onConfirm();
  }, [onConfirm, dismissDialog]);

  return [
    (
      <Dialog modalType={modalType} open={!additionalProps.hidden} onOpenChange={internalOnCancel}>
        <DialogSurface>
          <DialogBody className={classNames('dialog-confirmation', className)}>
            <DialogTitle>{passThroughProps.title}</DialogTitle>
            <DialogContent>
              {content}
            </DialogContent>
            <DialogActions fluid>
              {!hideConfirm &&
                <Button
                  className="dialog-confirm"
                  appearance="primary"
                  onClick={onConfirmClicked}
                  disabled={passThroughProps.isConfirmButtonDisabled}
                >
                  {passThroughProps.confirmText || i18n.getString(_TL_('Confirm'))}
                </Button>}
              {secondaryButtons.map(buttonProps => (
                <Button
                  appearance="secondary"
                  {...buttonProps}
                  buttonKey={buttonProps.key}
                  onClick={buttonProps.onClick}
                />))
              }
              {!hideCancel &&
                <Button
                  className="dialog-cancel"
                  appearance="secondary"
                  onClick={internalOnCancel}
                >
                  {passThroughProps.cancelText || i18n.getString(_TL_('Cancel'))}
                </Button>}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    ),
    showDialog,
    !passThroughProps.hidden,
  ];
}
